/**
 * Доставка: заказы, курьеры, зоны.
 *
 * Карты с движущимися точками здесь нет — она требует приложения курьера
 * с постоянной передачей координат. Вместо неё оператор видит то, что
 * действительно помогает: кто свободен, кто перегружен и какой заказ опаздывает.
 *
 * Оформление 2.10: время — первая и самая крупная колонка; опоздание
 * названо числом («+13 мин»), а не заливкой всей строки; действия
 * разложены по важности, «Отменить» отделён от остальных.
 */
import { useState } from 'react';
import { useAskSheet, useNote } from './ui/ask';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { DELIVERY_STATUS_RU, type DeliveryStatus, nextStatuses } from '@dastarhan/core';

const API = '/api/v1/delivery';

type Row = {
  id: string; status: DeliveryStatus; guestName: string; phone: string;
  address: string; comment: string | null; orderSum: number; deliveryFee: number;
  etaMin: number; minutes: number; late: boolean;
  courier: { id: string; name: string; phone: string; transport: string } | null;
  createdAt: string; deliveredAt: string | null; cancelReason: string | null;
};
type Courier = {
  id: string; name: string; phone: string;
  transport: 'FOOT' | 'BIKE' | 'CAR'; onShift: boolean; load: number;
};
type Zone = {
  id: string; name: string; radiusKm: number; price: number;
  freeFrom: number | null; etaMin: number; minSum: number;
};

const money = (v: number) => `${Math.round(v / 100).toLocaleString('ru-RU')} ₸`;
const TRANSPORT: Record<string, string> = { FOOT: 'пешком', BIKE: 'вело', CAR: 'авто' };

async function call<T>(path: string, token: string, opts: { method?: string; body?: unknown } = {}): Promise<T> {
  const res = await fetch(`${API}${path}`, {
    method: opts.method ?? 'GET',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: opts.body ? JSON.stringify(opts.body) : undefined,
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json() as Promise<T>;
}

/** Главная колонка: сколько идёт и сколько обещали гостю. */
function TimeCell({ o }: { o: Row }) {
  const over = o.minutes - o.etaMin;
  const done = o.status === 'DELIVERED' || o.status === 'CANCELLED';
  return (
    <div className={`dl-time ${o.late && !done ? 'late' : ''}`}>
      <div className="dl-min">
        <b data-money>{o.minutes}</b><span>мин</span>
      </div>
      {o.late && !done && <span className="dl-over" data-money>+{over} мин</span>}
      <span className="dl-eta">
        {done ? 'завершён' : o.late ? `обещали ${o.etaMin}` : `обещали ${o.etaMin} · осталось ${o.etaMin - o.minutes}`}
      </span>
    </div>
  );
}

export function DeliveryScreen({ token, locationId }: { token: string; locationId: string }) {
  const { ask, sheet } = useAskSheet();
  const { note, noteView } = useNote();
  const qc = useQueryClient();
  const [tab, setTab] = useState<'orders' | 'couriers' | 'zones'>('orders');
  const [showAll, setShowAll] = useState(false);
  const [assigning, setAssigning] = useState<Row | null>(null);

  const orders = useQuery({
    queryKey: ['dl-orders', locationId, showAll],
    queryFn: () => call<Row[]>(`?locationId=${locationId}${showAll ? '&all=1' : ''}`, token),
    refetchInterval: 15_000,
  });

  const couriers = useQuery({
    queryKey: ['dl-couriers'],
    queryFn: () => call<Courier[]>('/couriers', token),
    refetchInterval: 30_000,
  });

  const move = useMutation({
    mutationFn: (v: { id: string; to: DeliveryStatus; courierId?: string; reason?: string }) =>
      call('/move', token, { method: 'POST', body: v }),
    onSuccess: () => {
      setAssigning(null);
      void qc.invalidateQueries({ queryKey: ['dl-orders'] });
      void qc.invalidateQueries({ queryKey: ['dl-couriers'] });
    },
  });

  const active = (orders.data ?? []).filter(
    (o) => o.status === 'NEW' || o.status === 'ASSIGNED' || o.status === 'ON_WAY');
  const lateCount = active.filter((o) => o.late).length;
  const onShift = (couriers.data ?? []).filter((c) => c.onShift);
  const freeCount = onShift.filter((c) => c.load === 0).length;

  const act = (o: Row, to: DeliveryStatus) => {
    if (to === 'CANCELLED') {
      void (async () => {
        const a = await ask({
          title: 'Отменить доставку',
          sub: 'Причину увидит владелец в журнале — по ней потом разбирают жалобы.',
          effects: [
            ['Гость', o.guestName],
            ['Адрес', o.address],
            ['Сумма заказа', money(o.orderSum)],
            ...(o.courier ? [['Курьер', o.courier.name] as [string, string]] : []),
          ],
          value: {
            label: 'Почему отменяем',
            placeholder: 'Гость не отвечает, адрес вне зоны…',
            hint: 'без причины отмена не уйдёт',
          },
          danger: true,
          confirmLabel: 'Отменить доставку',
        });
        if (!a) return;
        move.mutate({ id: o.id, to, reason: a.value });
      })();
      return;
    }
    if (to === 'ASSIGNED') { setAssigning(o); return; }
    move.mutate({ id: o.id, to });
  };

  const label = (to: DeliveryStatus) =>
    to === 'ASSIGNED' ? 'Назначить курьера'
      : to === 'ON_WAY' ? 'Выехал'
        : to === 'DELIVERED' ? 'Доставлен'
          : to === 'NEW' ? 'Снять' : 'Отменить';

  return (
    <section className="menu-cat delivery-screen">
      <header>
        <h2>Доставка</h2>
        <div className="plan-tools">
          {(['orders', 'couriers', 'zones'] as const).map((t) => (
            <button key={t} className={`chip ${tab === t ? 'chip-on' : ''}`} onClick={() => setTab(t)}>
              {t === 'orders' ? `Заказы${active.length ? ` · ${active.length}` : ''}`
                : t === 'couriers' ? 'Курьеры' : 'Зоны'}
            </button>
          ))}
        </div>
      </header>

      {tab === 'orders' && (
        <>
          <div className="dl-bar">
            {lateCount > 0 && (
              <div className="dl-alarm">
                <b data-money>{lateCount}</b>
                <div>
                  <span className="dl-alarm-title">Опаздывают</span>
                  <span className="dl-alarm-note">дольше обещанного гостю</span>
                </div>
              </div>
            )}
            <div className="dl-free">
              <span>Свободных курьеров</span>
              <b data-money>{freeCount}</b>
              <span>из {onShift.length} на смене</span>
            </div>
            <label className="dl-check">
              <input type="checkbox" checked={showAll} onChange={(e) => setShowAll(e.target.checked)} />
              Показать завершённые за сутки
            </label>
          </div>

          {/* Грид вместо <table> — ради карточек на 390 без дублирования разметки.
              Семантику таблицы возвращаем ролями: без них незрячий оператор
              услышит сплошной поток слов вместо строк и колонок. */}
          <div className="dl-table" role="table" aria-label="Активные доставки">
            <div className="dl-row head" role="row">
              <span role="columnheader">Время в работе</span>
              <span role="columnheader">Статус</span>
              <span role="columnheader">Адрес</span>
              <span role="columnheader">Гость</span>
              <span role="columnheader" className="num">Сумма</span>
              <span role="columnheader">Курьер</span>
              <span role="columnheader">Действие</span>
            </div>

            {(orders.data ?? []).map((o) => {
              // Неизвестный статус — не повод ронять весь список заказов
            const nexts = nextStatuses(o.status) ?? [];
              const main = nexts.find((t) => t === 'DELIVERED' || t === 'ON_WAY' || t === 'ASSIGNED');
              const rest = nexts.filter((t) => t !== main && t !== 'CANCELLED');
              const canCancel = nexts.includes('CANCELLED');
              return (
                <div key={o.id} className={`dl-row ${o.late ? 'dl-late' : ''}`} role="row">
                  <div className="dl-timecell" role="cell"><TimeCell o={o} /></div>
                  <span className={`badge dl-${o.status.toLowerCase()}`} role="cell">{DELIVERY_STATUS_RU[o.status]}</span>
                  <div className="dl-addr" role="cell">
                    <b>{o.address}</b>
                    {o.comment && <span className="sub">{o.comment}</span>}
                  </div>
                  <div className="dl-guest" role="cell">
                    <span>{o.guestName}</span>
                    <a href={`tel:${o.phone}`}>{o.phone}</a>
                  </div>
                  <div className="num dl-sum" role="cell">
                    <b data-money>{money(o.orderSum)}</b>
                    {o.deliveryFee > 0 && <span className="sub" data-money>+{money(o.deliveryFee)} доставка</span>}
                  </div>
                  <div className="dl-courier" role="cell">
                    {o.courier ? (
                      <>
                        <span>{o.courier.name}</span>
                        <span className="sub">{TRANSPORT[o.courier.transport]}</span>
                      </>
                    ) : <span className="sub">не назначен</span>}
                  </div>
                  <div className="dl-acts" role="cell">
                    {main && (
                      <button className={`btn small ${main === 'DELIVERED' ? 'done' : 'accent'}`}
                        onClick={() => act(o, main)}>{label(main)}</button>
                    )}
                    {rest.map((to) => (
                      <button key={to} className="btn small" onClick={() => act(o, to)}>{label(to)}</button>
                    ))}
                    {canCancel && (
                      <button className="dl-cancel" onClick={() => act(o, 'CANCELLED')}>Отменить</button>
                    )}
                  </div>
                </div>
              );
            })}

            {(orders.data ?? []).length === 0 && (
              <p className="note">{showAll ? 'За сутки доставок не было' : 'Активных доставок нет'}</p>
            )}
          </div>
        </>
      )}

      {tab === 'couriers' && <Couriers token={token} data={couriers.data ?? []} />}
      {tab === 'zones' && <Zones token={token} locationId={locationId} />}

      {assigning && (
        <div className="dialog">
          <div className="dialog-card">
            <h2>Кому отдать заказ</h2>
            <p className="hint">{assigning.address}</p>
            {onShift.length === 0 && (
              <p className="rb-warn">Ни один курьер не на смене — отметьте смену во вкладке «Курьеры».</p>
            )}
            <div className="assign-pool">
              {[...onShift].sort((a, b) => a.load - b.load).map((c) => (
                <button key={c.id} className={`assign-item ${c.load > 0 ? 'busy' : ''}`}
                  onClick={() => move.mutate({ id: assigning.id, to: 'ASSIGNED', courierId: c.id })}>
                  <b>{c.name}</b>
                  <span>{TRANSPORT[c.transport]}</span>
                  <i className={c.load > 0 ? 'load' : 'free'}>
                    {c.load > 0 ? `на руках ${c.load}` : 'свободен'}
                  </i>
                </button>
              ))}
            </div>
            <button className="btn" onClick={() => setAssigning(null)}>Отмена</button>
          </div>
        </div>
      )}
      {sheet}
      {noteView}
    </section>
  );
}

function Couriers({ token, data }: { token: string; data: Courier[] }) {
  const { ask, sheet } = useAskSheet();
  const { note, noteView } = useNote();
  const qc = useQueryClient();
  const [form, setForm] = useState({ name: '', phone: '', transport: 'FOOT' as const });

  const save = useMutation({
    mutationFn: () => call('/couriers', token, { method: 'POST', body: form }),
    onSuccess: () => {
      setForm({ name: '', phone: '', transport: 'FOOT' });
      void qc.invalidateQueries({ queryKey: ['dl-couriers'] });
    },
  });
  const shift = useMutation({
    mutationFn: (id: string) => call('/couriers/shift', token, { method: 'POST', body: { id } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['dl-couriers'] }),
  });
  const remove = useMutation({
    mutationFn: (id: string) => call('/couriers/remove', token, { method: 'POST', body: { id } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['dl-couriers'] }),
  });

  return (
    <>
      <div className="crr-table" role="table" aria-label="Курьеры">
        <div className="crr-row head" role="row">
          <span role="columnheader">Имя</span>
          <span role="columnheader">Телефон</span>
          <span role="columnheader">Как ездит</span>
          <span role="columnheader">Смена</span>
          <span role="columnheader">На руках</span>
          <span role="columnheader" aria-label="Действие" />
        </div>
        {data.map((c) => (
          <div key={c.id} className={`crr-row ${c.onShift ? '' : 'off'}`} role="row">
            <b role="cell">{c.name}</b>
            <a href={`tel:${c.phone}`} role="cell">{c.phone}</a>
            <span role="cell">{TRANSPORT[c.transport]}</span>
            <span className="crr-act" role="cell">
              <button className={`chip ${c.onShift ? 'chip-on' : ''}`} onClick={() => shift.mutate(c.id)}>
                {c.onShift ? 'на смене' : 'не на смене'}
              </button>
            </span>
            <span className="crr-load" role="cell">{c.load > 0 ? <b data-money>{c.load}</b> : <i>свободен</i>}</span>
            <span className="crr-act" role="cell">
              <button className="dl-cancel" onClick={() => void (async () => {
                const a = await ask({
                  title: `Убрать курьера ${c.name}`,
                  sub: c.load > 0
                    ? 'На нём сейчас есть заказы — их придётся передать другому.'
                    : 'Свободен: заказов на нём нет.',
                  effects: [
                    ['Курьер', c.name],
                    ['Заказов на руках', String(c.load)],
                    ['История доставок', 'сохранится'],
                  ],
                  danger: true,
                  confirmLabel: 'Убрать',
                });
                if (a) remove.mutate(c.id);
              })()}>Убрать</button>
            </span>
          </div>
        ))}
        {data.length === 0 && <p className="note">Курьеров пока нет</p>}
      </div>

      <div className="dl-form">
        <input placeholder="Имя" value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })} />
        <input placeholder="+7 701 123-45-67" value={form.phone}
          onChange={(e) => setForm({ ...form, phone: e.target.value })} />
        <select value={form.transport}
          onChange={(e) => setForm({ ...form, transport: e.target.value as 'FOOT' })}>
          <option value="FOOT">пешком</option>
          <option value="BIKE">вело</option>
          <option value="CAR">авто</option>
        </select>
        <button className="btn accent" disabled={form.name.length < 2 || form.phone.length < 10}
          onClick={() => save.mutate()}>Добавить курьера</button>
      </div>
      {sheet}
      {noteView}
    </>
  );
}

function Zones({ token, locationId }: { token: string; locationId: string }) {
  const qc = useQueryClient();
  const { data } = useQuery({
    queryKey: ['dl-zones', locationId],
    queryFn: () => call<Zone[]>(`/zones?locationId=${locationId}`, token),
  });
  const [draft, setDraft] = useState<Zone[] | null>(null);
  const rows = draft ?? data ?? [];

  const save = useMutation({
    mutationFn: () => call('/zones', token, {
      method: 'POST',
      body: {
        locationId,
        zones: rows.map((z) => ({
          name: z.name, radiusKm: z.radiusKm, price: z.price,
          freeFrom: z.freeFrom, etaMin: z.etaMin, minSum: z.minSum,
        })),
      },
    }),
    onSuccess: () => {
      setDraft(null);
      void qc.invalidateQueries({ queryKey: ['dl-zones'] });
    },
  });

  const patch = (i: number, p: Partial<Zone>) =>
    setDraft(rows.map((z, j) => (i === j ? { ...z, ...p } : z)));

  // Кольца в масштабе: самое дальнее задаёт 100 % диаметра схемы
  const maxKm = Math.max(1, ...rows.map((z) => z.radiusKm));

  return (
    <div className="zones-wrap">
      <div className="zones-map">
        {/* Схема, а не карта: концентрические кольца вокруг заведения.
            Полигонов сознательно нет — кольцо владелец задаёт за минуту. */}
        <div className="rings" role="img" aria-label="Кольца зон доставки вокруг заведения">
          {[...rows].sort((a, b) => b.radiusKm - a.radiusKm).map((z, i) => {
            const size = Math.max(14, Math.round((z.radiusKm / maxKm) * 100));
            return (
              <div key={z.id || i} className="ring" style={{ width: `${size}%`, height: `${size}%` }}>
                <span className="ring-label">{z.name} · {z.radiusKm} км</span>
              </div>
            );
          })}
          <span className="ring-center" />
        </div>
        <p className="hint">
          Зона — кольцо вокруг заведения. Адрес попадает в первое кольцо,
          в которое укладывается расстояние. За последним кольцом заказ
          не принимается — и гость узнаёт об этом сразу, а не после готовки.
        </p>
      </div>

      <div className="zones-table" role="table" aria-label="Зоны доставки">
        <div className="zone-row head" role="row">
          <span role="columnheader">Название</span>
          <span role="columnheader" className="num">Радиус, км</span>
          <span role="columnheader" className="num">Доставка, ₸</span>
          <span role="columnheader" className="num">Бесплатно от, ₸</span>
          <span role="columnheader" className="num">Минимум, ₸</span>
          <span role="columnheader" className="num">Обещаем, мин</span>
          <span role="columnheader" aria-label="Действие" />
        </div>
        {rows.map((z, i) => (
          <div key={z.id || i} className="zone-row" role="row">
            <div className="zone-name" role="cell">
              <i className="zone-dot" />
              <input value={z.name} aria-label="Название зоны"
                onChange={(e) => patch(i, { name: e.target.value })} />
            </div>
            <input type="number" step="0.5" value={z.radiusKm} aria-label="Радиус, км" role="cell"
              onChange={(e) => patch(i, { radiusKm: Number(e.target.value) })} />
            <input type="number" value={z.price / 100} aria-label="Стоимость доставки" role="cell"
              onChange={(e) => patch(i, { price: Number(e.target.value) * 100 })} />
            <input type="number" value={z.freeFrom === null ? '' : z.freeFrom / 100}
              placeholder="всегда платно" aria-label="Бесплатно от" role="cell"
              onChange={(e) => patch(i, { freeFrom: e.target.value === '' ? null : Number(e.target.value) * 100 })} />
            <input type="number" value={z.minSum / 100} aria-label="Минимальная сумма" role="cell"
              onChange={(e) => patch(i, { minSum: Number(e.target.value) * 100 })} />
            <input type="number" value={z.etaMin} aria-label="Обещаем, мин" role="cell"
              onChange={(e) => patch(i, { etaMin: Number(e.target.value) })} />
            <span className="crr-act" role="cell">
              <button className="dl-cancel" onClick={() => setDraft(rows.filter((_, j) => j !== i))}>Убрать</button>
            </span>
          </div>
        ))}
        {rows.length === 0 && <p className="note">Зоны не настроены — доставка не принимается</p>}

        <div className="dl-form">
          <button className="btn" onClick={() => setDraft([...rows, {
            id: '', name: `Зона ${rows.length + 1}`,
            radiusKm: (rows.at(-1)?.radiusKm ?? 0) + 3,
            price: 50_000, freeFrom: null, etaMin: 45, minSum: 200_000,
          }])}>+ Кольцо</button>
          <button className="btn accent" disabled={!draft || save.isPending}
            onClick={() => save.mutate()}>Сохранить зоны</button>
        </div>
      </div>
    </div>
  );
}
